import { ThumbUp } from '@mui/icons-material';
import { Button, ButtonGroup } from '@mui/material';
import { Fragment, useMemo, useState } from 'react';

import { useTogglePostLike } from 'shared/features/posts/hooks';
import { useHomeFeedCardData, useStreamActivityContext } from 'shared/features/posts/providers';
import { useCurrentUser, useModalState, useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

import { HomeFeedCardLikedModal } from './HomeFeedCardLikedModal';

interface Reaction {
  id: string;
  user_id: string;
}

export function HomeFeedCardLikeButton() {
  const { data: activity } = useStreamActivityContext();
  const { toggle } = useTogglePostLike();
  const { user } = useCurrentUser();
  const { showError } = useToast();

  const [reactions, setReactions] = useState<Reaction[]>(activity?.latest_reactions?.like ?? []);

  const {
    show: showWhoLikes,
    haveBeenOpened: haveWhoLikesBeenOpened,
    open: openWhoLikes,
    close: closeWhoLikes,
  } = useModalState();

  const post = useHomeFeedCardData();

  const currentUserReaction = useMemo(() => {
    return reactions.find(reaction => reaction.user_id === user?.id);
  }, [user, reactions]);

  const hasCurrentUserLike = Boolean(currentUserReaction);

  const handleToggleLike = async () => {
    try {
      // Change the state optimistically before the response arrives.
      setReactions(prev => {
        return hasCurrentUserLike
          ? prev.filter(reaction => reaction.user_id !== user?.id)
          : [...prev, { id: String(), user_id: String(user?.id) }];
      });

      await toggle(post);
    } catch (err) {
      recordError(err);
      // Revert the optimistic update if the mutation fails.
      setReactions(activity?.latest_reactions?.like ?? []);

      if (err instanceof Error) {
        showError(err.message);
      }
    }
  };

  const handleOpenWhoLikes = () => {
    openWhoLikes();
  };

  return (
    <Fragment>
      <ButtonGroup>
        <Button
          variant="text"
          size="small"
          onClick={handleToggleLike}
          color={hasCurrentUserLike ? 'secondary' : 'primary'}
        >
          <ThumbUp fontSize="small" />
        </Button>

        <Button
          size="small"
          variant="text"
          color={hasCurrentUserLike ? 'secondary' : 'primary'}
          onClick={handleOpenWhoLikes}
        >
          {reactions.length}
        </Button>
      </ButtonGroup>

      <HomeFeedCardLikedModal
        show={showWhoLikes}
        haveBeenOpened={haveWhoLikesBeenOpened}
        onClose={closeWhoLikes}
      />
    </Fragment>
  );
}
